import TenantsList from "@/components/SwitchAccount/modals/TenantsList";
import {firebase, firestore} from "@/config/firebase";
import {mapState} from "vuex";
import AccountMixin from "@/mixins/account";

export default {
  name: "SwitchAccount",
  props: {},
  mixins: [AccountMixin],
  components: {
    TenantsList
  },
  computed: {
    ...mapState({
      tenant_id: state => state.Chat.tenant_id,
      device_id: state => state.Chat.device_id,
      owner_id: state => state.Chat.owner_id,
      user_doc: state => state.Chat.user_doc,
      user: state => state.Chat.logged,
    }),
    activeTenants() {
      if (!this.user || !this.user.tenants_list) return [];
      const activeIds = this.user.tenants_list
        .filter(t => t.status !== 'inactive')
        .map(t => t.tenant_id);
      return this.tenants.filter(t => activeIds.includes(t.tenant_id));
    },
  },
  data() {
    return {
      showTenantsList: false,
      tenants: [],
      subscriptions: [],
      totalNotifications: 0
    }
  },
  watch: {
    user(newVal) {
      if (newVal) {
        this.initTenantsList();
        this.unsubscribe();
        this.initSubscriptions();
      } else {
        this.unsubscribe();
      }
    }
  },
  created() {
    this.initTenantsList();
  },
  methods: {
    async initTenantsList() {
      this.tenants = [];
      this._tenantsLoadId = Date.now();
      const loadId = this._tenantsLoadId;
      if (this.user && this.user.tenants_list) {
        let tenant_ids = [...new Set(this.user.tenants_list.map(t => t.tenant_id))];

        let size = 10;
        let tenant_ids_split = [];
        for (let i = 0; i < tenant_ids.length; i += size) {
          tenant_ids_split.push(tenant_ids.slice(i, i + size));
        }

        let subusers = {};
        for (const tid of tenant_ids) {
          const subUserSnap = await firestore
            .collection("tenants").doc(tid).collection("users")
            .where("owner_id", "==", this.user.owner_id)
            .get().catch(error => console.error(error));
          if (subUserSnap) {
            for (const doc of subUserSnap.docs) {
              subusers[doc.data().tenant_id] = { ...doc.data(), id: doc.id };
            }
          }
        }

        for (const tenant_ids of tenant_ids_split) {
          const tenantsRef = await firestore
            .collection("tenants")
            .where("id", "in", tenant_ids)
            .get().catch(error => console.error(error));
          if (!tenantsRef) continue;
          for (const tenantRef of tenantsRef.docs) {
            let tenant = {};
            if (this.user.owner_id !== tenantRef.data().owner_id) {
              const subuser = subusers[tenantRef.id];
              if (subuser) {
                tenant = this.user.tenants_list.find(x => x.tenant_id === tenantRef.id && x.role === subuser.user_role);
                // Fallback: match by tenant_id only (covers super_user where roles may differ)
                if (!tenant) {
                  tenant = this.user.tenants_list.find(x => x.tenant_id === tenantRef.id);
                }
                if (subuser.is_active === false) {
                  continue;
                }
              } else {
                // No sub-user doc found — try matching by tenant_id only (super_user)
                tenant = this.user.tenants_list.find(x => x.tenant_id === tenantRef.id);
              }
            } else {
              tenant = this.user['tenants_list'].find(x => x.tenant_id === tenantRef.id && x.role === "owner");
            }
            if (!tenant) continue;
            let canAddTenant = false;
            if (["agent", "vendor", "employee"].includes(tenant.role) && tenant.status === "inactive") {
              canAddTenant = false;
            } else if ((tenant.status === undefined || tenant.status !== 'inactive')) {
              canAddTenant = true;
            }

            if (canAddTenant) {
              const companyName = tenantRef.data().company_name || tenant.company_name;
              if (!this.tenants.find(x => x.tenant_id === tenant.tenant_id)) {
                this.tenants.push({
                  ...tenant,
                  company_name: companyName,
                  notifications: 0
                });
              }
            }
          }
        }

        // Discard results if a newer load started while we were fetching
        if (this._tenantsLoadId !== loadId) return;
        this.initSubscriptions();
      }
    }
    ,
    initSubscriptions() {
      this.unsubscribe();
      const self = this;
      this.subscriptions.push(firestore.collection("notifications").doc(this.user.owner_id)
        .onSnapshot(doc => {
          if (doc.exists) {
            const tenant_badge = doc.data().tenant_badge;
            self.totalNotifications = 0;
            for (let tenant of self.tenants) {
              if (tenant.tenant_id !== self.tenant_id) {
                tenant.notifications = tenant_badge[tenant.tenant_id] ? tenant_badge[tenant.tenant_id] : 0;
                self.totalNotifications += tenant.notifications;
              }
            }
          }
        }, error => {
          console.error('[SwitchAccount] Notifications listener error:', error);
        }));
    }
    ,
    unsubscribe() {
      for (const subscription of this.subscriptions) {
        subscription ? subscription() : '';
      }
      // Reset so initSubscriptions() doesn't accumulate cancelled
      // handles across tenant switches / user watcher re-runs.
      this.subscriptions = [];
    }
    ,
    showModal() {
      this.showTenantsList = true;
    }
    ,
    hideModal() {
      this.showTenantsList = false;
    }
    ,
    async onSelectItem(tenant) {
      const self = this;
      if (tenant.tenant_id !== this.tenant_id) {
        const user_docRef = await firestore
          .collection('tenants')
          .doc(tenant.tenant_id)
          .collection("users")
          .where("owner_id", "==", self.owner_id).limit(1).get();

        if (!user_docRef.docs.length) {
          console.warn('[SwitchAccount] Orphan tenant entry (no sub-user doc), removing from switcher:', tenant.tenant_id);
          self.tenants = self.tenants.filter(t => t.tenant_id !== tenant.tenant_id);
          self.hideModal();
          if (window && window.EventBus) {
            window.EventBus.$emit('snackbar', {
              color: 'warning',
              message: 'That account appears orphaned and has been removed from your switcher.'
            });
          }
          return;
        }

        const user_doc = user_docRef.docs[0].id;
        const owner_id = self.owner_id;
        const tenant_id = tenant.tenant_id;
        const device_id = self.device_id;

        try {
          // Update device doc to new tenant before generating token
          await firestore.collection("devices").doc(device_id).update({
            tenant_id: tenant_id,
            user_doc: user_doc
          });

          const web_agent_id = self.$store.state.Chat.token;
          self.$store.dispatch("Chat/generate_firebase_token", {
            user_id: this.owner_id,
            tenant_id: tenant.tenant_id,
            web_agent_id: web_agent_id
          }).then(response => {
            if (!response) return;
            firebase.auth().signInWithCustomToken(response.customToken).then(() => {

              localStorage.setItem("auth_id", response.token);
              localStorage.setItem("auth_token", response.token);
              self.$store.commit("Authenticate/auth_success", { access_token: response.token, user: self.$store.state.Authenticate.user });

              // Clear state BEFORE changing tenant/owner to prevent stale data
              self.$store.commit("Chat/conversations", []);
              self.$store.commit("Chat/files", []);
              self.$store.commit("Chat/setUxDocs", {});
              self.$store.dispatch("Users_list/clear_data", { fullReset: true });
              self.$store.commit("Users_list/setUxDocs", []);
              self.$store.commit("Users_list/setUxTenantDocs", []);

              // Set new tenant context all at once before triggering any re-init
              self.$store.commit("Chat/tenant", tenant_id);
              self.$store.commit("Chat/tenant_company_name", tenant.company_name || '');
              self.$store.commit("Chat/user_doc", user_doc);
              self.$store.commit("Chat/device_id", device_id);
              self.$store.commit("Chat/owner_id", owner_id);

              self.$store.dispatch("Chat/logged");

              localStorage.removeItem("conversations");
              localStorage.removeItem("uxDocs");

              window.EventBus.$emit("reload");
            })
              .catch((error) => {
                console.error(error);
              });
          }).catch((error) => {
            console.error('[SwitchAccount] generate_firebase_token failed:', error);
          });
        } catch (errors) {
          console.error(errors)
        }
      }
    }
    ,
    confirmAccount(tenant) {
      this.hideModal();
      this.onSelectItem(tenant);
    }
  }
}